import { AddRounded, Delete, Edit, Save } from '@mui/icons-material'
import {
    Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, Paper, Stack, Table, TableBody,
    TableCell, TableContainer, TableHead, TableRow, TextField, Tooltip, Typography
} from '@mui/material'
import { useEffect, useState } from 'react'
import ApiClient from '../../backend/ApiClient'
import { ITooltipData } from '../../definitions/Tooltip'
import Helper from '../../helpers/Helper'
import TooltipsManager from '../../managers/TooltipsManager'
import useInvalidate from '../hooks/useInvalidate'
import useQuery, { buildQueryKey } from '../hooks/useQuery'
import { modals } from '../services/ModalService'
import { snackbar } from '../services/SnackbarService'
import { tooltipCache } from '../controls/SmartTooltip'
import { LoadingButton } from '@mui/lab'

function TooltipsAdmin() {
    const [tooltips, setTooltips] = useState<ITooltipData[]>([])
    const [error, setError] = useState<string | null>(null)
    const [open, setOpen] = useState(false)
    const [loading, setLoading] = useState<boolean>(false)
    const [tooltipData, setTooltipData] = useState<ITooltipData | null>(null)
    const [newTooltip, setNewTooltip] = useState<ITooltipData>({
        tooltipKey: '',
        title: '',
        description: '',
        uuid: Helper.generateUUID('tooltip'),
        _signature: null
    })

    const invalidate = useInvalidate({ queryKey: buildQueryKey('get-tooltips') })
    const invalidateTooltipCacheKey = (key: string) => {
        tooltipCache.delete(key)
    }
    const isCreateDisabled: boolean = !newTooltip.tooltipKey.trim() || !newTooltip.title.trim() || !newTooltip.description.trim()

    const { status, response: allTooltips } = useQuery({
        queryKey: buildQueryKey('get-tooltips'),
        queryFn: () => ApiClient.getAllTooltips(),
        cache: true
    })

    useEffect(() => {
        if (status === 'success' && allTooltips) {
            setTooltips(allTooltips)
        } else if (status === 'error') {
            setError('Failed to fetch tooltips')
        }
    }, [status])

    const handleDelete = async (uuid: string) => {
        modals.openConfirm({
            title: 'Confirm Tooltip Removal',
            children: <p>Are you sure you want to remove the tooltip?</p>,
            labels: { confirm: 'Yes, Delete', cancel: 'Cancel' },
            colors: { confirm: 'error', cancel: 'primary' },
            onConfirm: async () => {
                try {
                    await TooltipsManager.deleteTooltip(uuid)
                    setTooltips(prev => prev.filter(tooltip => tooltip.uuid !== uuid))
                    invalidate()
                } catch (error) {
                    setError('Failed to delete tooltip: ' + error)
                }
            }
        })
    }

    const handleCreateNew = () => {
        setTooltipData(null)
        setNewTooltip({
            tooltipKey: '',
            title: '',
            description: '',
            uuid: Helper.generateUUID('tooltip'),
            _signature: null
        })
        setOpen(true)
    }

    const handleOpen = (tooltip: ITooltipData) => {
        setTooltipData(tooltip)
        setOpen(true)
    }

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault()

        const formattedKey = newTooltip.tooltipKey.trim().replace(/\s+/g, '_')

        if (allTooltips.some(tooltip => tooltip.tooltipKey === formattedKey)) {
            snackbar.show({ severity: 'error', text: 'A tooltip with this key already exists.' })
            return
        }

        const finalTooltip: ITooltipData = {
            ...newTooltip,
            tooltipKey: formattedKey,
            uuid: formattedKey
        }
        try {
            setLoading(true)
            const createdTooltip = await TooltipsManager.createTooltip(finalTooltip)
            setTooltips(prev => [...prev, createdTooltip])
            setOpen(false)
            setNewTooltip({ tooltipKey: '', title: '', description: '', uuid: '', _signature: null })
            invalidate()
            setLoading(false)
        } catch (error) {
            setError('Failed to create tooltip: ' + error)
        }
    }

    const handleUpdate = async () => {
        if (tooltipData) {
            const updatedTooltip: ITooltipData = {
                ...tooltipData,
                tooltipKey: tooltipData.tooltipKey.trim().replace(/\s+/g, '_')
            }

            if (allTooltips.some(tooltip => tooltip.tooltipKey === updatedTooltip.tooltipKey && tooltip.uuid !== updatedTooltip.uuid)) {
                snackbar.show({ severity: 'error', text: 'A tooltip with this key already exists.' })
                return
            }

            try {
                setLoading(true)
                await TooltipsManager.updateTooltip(updatedTooltip)
                setTooltips(prev =>
                    prev.map(tooltip => (tooltip.uuid === updatedTooltip.uuid ? updatedTooltip : tooltip))
                )
                setOpen(false)
                setTooltipData(null)
                invalidateTooltipCacheKey(updatedTooltip.tooltipKey)
                invalidate()
                setLoading(false)
            } catch (error) {
                setError('Failed to update tooltip : ' + error)
            }
        }
    }

    if (status === 'loading') return <CircularProgress />
    if (error) return <Typography color='error'>{error}</Typography>

    return (
        <Stack spacing={2}>
            <Stack direction='row' justifyContent='space-between'>
                <Typography variant='h5'>
                    <strong>Tooltips List</strong>
                </Typography>
                <Button
                    variant='contained'
                    color='primary'
                    startIcon={(<AddRounded />)}
                    onClick={handleCreateNew}>
                    <Typography variant='button' paddingRight={0.5}>
                        New Tooltip
                    </Typography>
                </Button>
            </Stack>

            <TableContainer component={Paper}>
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell>
                                <strong>Key</strong>
                            </TableCell>
                            <TableCell>
                                <strong>Title</strong>
                            </TableCell>
                            <TableCell>
                                <strong>Description</strong>
                            </TableCell>
                            <TableCell>
                                <strong>Actions</strong>
                            </TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {tooltips?.map(tooltip => (
                            <TableRow key={tooltip.tooltipKey}>
                                <TableCell>{tooltip.tooltipKey}</TableCell>
                                <TableCell>{tooltip.title}</TableCell>
                                <TableCell>{tooltip.description}</TableCell>
                                <TableCell>
                                    <Stack direction='row' spacing={1}>
                                        <IconButton
                                            onClick={() => handleOpen(tooltip)}>
                                            <Tooltip title='Edit' arrow>
                                                <Edit />
                                            </Tooltip>
                                        </IconButton>
                                        <IconButton
                                            color='error'
                                            onClick={() => handleDelete(tooltip.uuid)}>
                                            <Tooltip title='Delete' arrow>
                                                <Delete />
                                            </Tooltip>
                                        </IconButton>
                                    </Stack>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
            <Dialog open={open} onClose={() => setOpen(false)}>
                <DialogTitle>{tooltipData ? 'Edit Tooltip' : 'Create New Tooltip'}</DialogTitle>
                <DialogContent>
                    <TextField
                        label='Tooltip Key'
                        fullWidth
                        value={tooltipData ? tooltipData.tooltipKey : newTooltip.tooltipKey}
                        onChange={e => {
                            if (tooltipData) {
                                setTooltipData({ ...tooltipData, tooltipKey: e.target.value })
                            } else {
                                setNewTooltip({ ...newTooltip, tooltipKey: e.target.value })
                            }
                        }}
                        margin='normal'
                    />
                    <TextField
                        label='Title'
                        fullWidth
                        value={tooltipData ? tooltipData.title : newTooltip.title}
                        onChange={e => {
                            if (tooltipData) {
                                setTooltipData({ ...tooltipData, title: e.target.value })
                            } else {
                                setNewTooltip({ ...newTooltip, title: e.target.value })
                            }
                        }}
                        margin='normal'
                    />
                    <TextField
                        label='Description'
                        fullWidth
                        value={tooltipData ? tooltipData.description : newTooltip.description}
                        onChange={e => {
                            if (tooltipData) {
                                setTooltipData({ ...tooltipData, description: e.target.value })
                            } else {
                                setNewTooltip({ ...newTooltip, description: e.target.value })
                            }
                        }}
                        margin='normal'
                        multiline
                    />
                </DialogContent>
                <DialogActions>
                    <Button color='primary' variant='outlined' onClick={() => setOpen(false)}>
                        Cancel
                    </Button>
                    <LoadingButton
                        loading={loading}
                        color='primary'
                        variant='contained'
                        onClick={tooltipData ? handleUpdate : handleSubmit}
                        startIcon={<Save />}
                        disabled={tooltipData ? false : isCreateDisabled}>
                        {tooltipData ? 'Save Changes' : 'Create Tooltip'}
                    </LoadingButton>
                </DialogActions>
            </Dialog>
        </Stack>
    )
}

export default TooltipsAdmin
